import type { ParagraphRunsMeta } from "../ooxml/extractText";
import {
  paragraphPlainTextFromRuns,
  runsHaveFormatting,
} from "../ooxml/textCore";
import { collectImportBlockElements } from "./collectBlocks";
import {
  blockHasInlineFormatting,
  runsToHtml,
  textsCompatible,
} from "./textHtml";
import type { TextFidelityStats } from "../types";

const EMPTY_STATS: TextFidelityStats = {
  paragraphsInDocx: 0,
  paragraphsInHtml: 0,
  paragraphsEnriched: 0,
  runsRebuilt: 0,
  semanticTagsNormalized: 0,
};

/**
 * Rebuild paragraph inline HTML from OOXML runs when mammoth lost formatting.
 * Runs before numbering merge so data-lexgat-num prefixes are not clobbered.
 */
export function applyTextMetaToHtml(
  html: string,
  paragraphRuns: ParagraphRunsMeta[],
): { html: string; stats: TextFidelityStats } {
  const stats: TextFidelityStats = { ...EMPTY_STATS };
  stats.paragraphsInDocx = paragraphRuns.length;

  if (!html.trim() || paragraphRuns.length === 0) {
    return { html, stats };
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(
    `<div id="text-root">${html}</div>`,
    "text/html",
  );
  const root = doc.getElementById("text-root");
  if (!root) return { html, stats };

  const blocks = collectImportBlockElements(root);
  stats.paragraphsInHtml = blocks.length;

  const limit = Math.min(blocks.length, paragraphRuns.length);
  for (let i = 0; i < limit; i++) {
    const el = blocks[i];
    const meta = paragraphRuns[i];
    if (!meta?.runs.length || !runsHaveFormatting(meta.runs)) continue;
    if (blockHasInlineFormatting(el)) continue;

    const runsText = paragraphPlainTextFromRuns(meta.runs);
    if (!textsCompatible(el.textContent ?? "", runsText)) continue;

    const rebuilt = runsToHtml(meta.runs);
    if (!rebuilt) continue;

    el.innerHTML = rebuilt;
    stats.paragraphsEnriched++;
    stats.runsRebuilt += meta.runs.filter((r) => r.text).length;
  }

  return { html: root.innerHTML.trim(), stats };
}
